import { ChangeEvent, ReactNode, useCallback, useEffect, useState } from "react";
import { Alert, Box, IconButton, InputAdornment, Stack, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import type { RuntimeConfig } from "../pages/ConfigPage";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import { useAuth } from "../context/AuthContext";

type LocalTabPanelProps = {
  children: ReactNode;
  value: number;
  index: number;
};

type AnalyticsConnectionState = "connected" | "disconnected" | "querying";

type SystemMonitorResponse = {
  hostInfo?: {
    analyticsDbSizeBytes?: number;
  };
};

const analyticsStateLabels: Record<AnalyticsConnectionState, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  querying: "Querying",
};

const AnalyticsForm = () => {
  const formik = useFormikContext<RuntimeConfig>();
  const { authorizedFetch, resolveApiPath } = useAuth();
  const analyticsErrors = formik.errors.analytics as Partial<Record<keyof RuntimeConfig["analytics"], string>> | undefined;
  const analyticsTouched = formik.touched.analytics as Partial<Record<keyof RuntimeConfig["analytics"], boolean>> | undefined;
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState(0);
  const [connectionState, setConnectionState] = useState<AnalyticsConnectionState>("querying");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [dbSizeBytes, setDbSizeBytes] = useState<number | null>(null);

  const fetchConnectionState = useCallback(async (payload: RuntimeConfig["analytics"]) => {
    setConnectionState("querying");
    setConnectionError(null);
    let hasDbSizeFromTest = false;
    try {
      const response = await authorizedFetch("/api/analytics/connection/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const rawText = await response.text();
      const parsed = (() => {
        if (!rawText) {
          return null;
        }
        try {
          return JSON.parse(rawText);
        } catch {
          return null;
        }
      })() as {
        state?: AnalyticsConnectionState;
        error?: string;
        dbSizeBytes?: number;
      } | null;

      if (!response.ok) {
        setConnectionState("disconnected");
        setDbSizeBytes(null);
        const fallback = rawText || response.statusText || `HTTP ${response.status}`;
        setConnectionError(parsed?.error ?? `Failed to test analytics DB connection (${fallback})`);
        return;
      }

      if (parsed?.state === "connected" || parsed?.state === "disconnected") {
        setConnectionState(parsed.state);
      } else {
        setConnectionState("disconnected");
        setDbSizeBytes(null);
      }
      if (parsed?.error) {
        setConnectionError(parsed.error);
      }
      if (parsed?.dbSizeBytes != null) {
        hasDbSizeFromTest = true;
        setDbSizeBytes(parsed.dbSizeBytes);
      }

    } catch (error) {
      setConnectionState("disconnected");
      setDbSizeBytes(null);
      const message = error instanceof Error ? error.message : "Failed to test analytics connection";
      setConnectionError(message);
    } finally {
      if (payload.enabled && !hasDbSizeFromTest) {
        try {
          const monitorResponse = await authorizedFetch(resolveApiPath("/api/server/system-monitor"));
          if (monitorResponse.ok) {
            const monitorData = (await monitorResponse.json().catch(() => null)) as SystemMonitorResponse | null;
            if (monitorData?.hostInfo?.analyticsDbSizeBytes != null) {
              setDbSizeBytes(monitorData.hostInfo.analyticsDbSizeBytes);
            }
          }
        } catch (err) {
          console.warn("Failed to fetch analytics DB size:", err);
        }
      }
    }
  }, [authorizedFetch, resolveApiPath]);

  useEffect(() => {
    // Only test connection if analytics is enabled and has connection details
    if (formik.initialValues.analytics.enabled && formik.initialValues.analytics.host) {
      void fetchConnectionState(formik.initialValues.analytics);
    }
  }, [
    fetchConnectionState,
    formik.initialValues.analytics.enabled,
    formik.initialValues.analytics.host,
    formik.initialValues.analytics.port,
    formik.initialValues.analytics.database,
    formik.initialValues.analytics.user
  ]);

  const LocalTabPanel = ({ children, value, index }: LocalTabPanelProps) => (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      sx={{ flex: 1, display: value === index ? "block" : "none" }}
    >
      {value === index && children}
    </Box>
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const getError = (field: keyof RuntimeConfig["analytics"]) => {
    if (!analyticsTouched?.[field]) {
      return undefined;
    }
    return analyticsErrors?.[field];
  };

  const handleNumberChange = (field: "port" | "dataRetentionDays" | "dataCompressionDays") => (event: ChangeEvent<HTMLInputElement>) => {
    const parsed = Number.parseInt(event.target.value, 10);
    void formik.setFieldValue(`analytics.${field}`, Number.isNaN(parsed) ? 0 : parsed);
  };

  const formatBytes = useCallback((value?: number) => {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
      return null;
    }
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let remaining = value;
    let index = 0;
    while (remaining >= 1024 && index < units.length - 1) {
      remaining /= 1024;
      index += 1;
    }
    if (remaining >= 100) {
      return `${remaining.toFixed(0)} ${units[index]}`;
    }
    if (remaining >= 10) {
      return `${remaining.toFixed(1)} ${units[index]}`;
    }
    return `${remaining.toFixed(2)} ${units[index]}`;
  }, []);

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleTabChange}
        aria-label="Analytics tabs"
        sx={{ borderRight: 1, borderColor: "divider", minWidth: 150 }}
      >
        <Tab label="TimescaleDB" id="analytics-tab-0" aria-controls="analytics-tabpanel-0" />
      </Tabs>
      <LocalTabPanel value={tab} index={0}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              TimescaleDB connection
            </Typography>
            <Tooltip title={analyticsStateLabels[connectionState]} placement="bottom">
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: (theme) => {
                    if (connectionState === "connected") {
                      return theme.palette.success.main;
                    }
                    if (connectionState === "querying") {
                      return theme.palette.warning.main;
                    }
                    return theme.palette.grey[500];
                  },
                  border: (theme) => `1px solid ${theme.palette.background.paper}`,
                }}
              />
            </Tooltip>
            <Tooltip title="Refresh status" placement="bottom">
              <span>
                <IconButton
                  aria-label="Refresh analytics connection state"
                  size="small"
                  onClick={() => void fetchConnectionState(formik.values.analytics)}
                  disabled={connectionState === "querying"}
                >
                  <RefreshIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {connectionError && <Alert severity="error">{connectionError}</Alert>}
          <TextField
            fullWidth
            label="DSN (optional)"
            name="analytics.dsn"
            size="small"
            value={formik.values.analytics.dsn}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={getError("dsn") || "Provide a PostgreSQL DSN to override the fields below"}
            error={Boolean(getError("dsn"))}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="Host"
              name="analytics.host"
              size="small"
              value={formik.values.analytics.host}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError("host"))}
              helperText={getError("host") || " "}
              sx={{ flex: "1 1 200px", minWidth: 200 }}
            />
            <TextField
              label="Port"
              name="analytics.port"
              type="number"
              size="small"
              value={formik.values.analytics.port || ""}
              onChange={handleNumberChange("port")}
              onBlur={formik.handleBlur}
              error={Boolean(getError("port"))}
              helperText={getError("port") || " "}
              sx={{ width: 140 }}
              inputProps={{ min: 1, max: 65535 }}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            <TextField
              label="Database"
              name="analytics.database"
              size="small"
              value={formik.values.analytics.database}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError("database"))}
              helperText={getError("database") || " "}
              sx={{ flex: "1 1 200px", minWidth: 200 }}
            />
            {dbSizeBytes !== null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, alignSelf: "flex-start", marginTop: "8px" }}>
                <StorageIcon sx={{ fontSize: 24, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Size:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {formatBytes(dbSizeBytes)}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="User"
              name="analytics.user"
              size="small"
              value={formik.values.analytics.user}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError("user"))}
              helperText={getError("user") || " "}
              sx={{ flex: "1 1 200px", minWidth: 200 }}
            />
            <TextField
              label="Password"
              name="analytics.password"
              type={showPassword ? "text" : "password"}
              size="small"
              value={formik.values.analytics.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError("password"))}
              helperText={getError("password") || " "}
              sx={{ flex: "1 1 200px", minWidth: 200 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="SSL mode"
              name="analytics.sslMode"
              size="small"
              value={formik.values.analytics.sslMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(getError("sslMode"))}
              helperText={getError("sslMode") || " "}
              sx={{ flex: "1 1 200px", minWidth: 200 }}
            />
            <TextField
              label="Data retention (days)"
              name="analytics.dataRetentionDays"
              type="number"
              size="small"
              value={formik.values.analytics.dataRetentionDays != null ? formik.values.analytics.dataRetentionDays : ""}
              onChange={handleNumberChange("dataRetentionDays")}
              onBlur={formik.handleBlur}
              error={Boolean(getError("dataRetentionDays"))}
              helperText={getError("dataRetentionDays") || "TimescaleDB retention for analytics data"}
              sx={{ flex: "1 1 220px", minWidth: 200 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Data compression (days)"
              name="analytics.dataCompressionDays"
              type="number"
              size="small"
              value={formik.values.analytics.dataCompressionDays != null ? formik.values.analytics.dataCompressionDays : ""}
              onChange={handleNumberChange("dataCompressionDays")}
              onBlur={formik.handleBlur}
              error={Boolean(getError("dataCompressionDays"))}
              helperText={getError("dataCompressionDays") || "TimescaleDB data compression for analytics data"}
              sx={{ flex: "1 1 220px", minWidth: 200 }}
              inputProps={{ min: 0 }}
            />
          </Box>
        </Stack>
      </LocalTabPanel>
    </Box>
  );
};

export default AnalyticsForm;
